<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl mb-5">
      {{ isEditing ? 'Edit Tug & Barge Performance' : 'View Tug & Barge Performance' }}
    </p>

    <!-- Header Information -->
    <div class="bg-white rounded-md p-5 mb-5">
      <vs-row vs-w="12">
        <vs-col vs-w="6">
          <p class="font-semibold mb-2">
            Contractor Name : {{ headerData.contractor_name }}
          </p>
        </vs-col>
        <vs-col vs-w="6">
          <p class="font-semibold mb-2">
            Contract Number : {{ headerData.contract_number }}
          </p>
        </vs-col>
        <vs-col vs-w="6">
          <p class="font-semibold mb-2">
            Contract Term : {{ headerData.contract_term }}
          </p>
        </vs-col>
        <vs-col vs-w="6">
          <p class="font-semibold mb-2">
            Evaluation Period : {{ headerData.evaluation_period }}
          </p>
        </vs-col>
      </vs-row>
    </div>

    <!-- Main Performance Evaluation Table -->
    <div class="bg-white rounded-md p-5 mb-5">
      <p class="font-semibold text-xl mb-4">
        Performance Evaluation
      </p>
      <data-table
        :table-header="tableHeaders"
        :table-data="performanceData"
        :grouped-headers="groupedHeaders"
        body-height="auto"
        :row-class="getRowClass"
        :loading="isBusy"
      >
        <template #performance_items="{ row }">
          <div
            :class="[
              'pl-2',
              row.level === 1 ? 'font-bold text-base' : '',
              row.level === 2 ? 'font-semibold' : '',
              row.level === 3 ? 'font-semibold' : ''
            ]"
          >
            {{ row.performance_items }}
          </div>
        </template>
        <template #unit="{ row }">
          <template v-if="row.level === 3 && row.unit">
            <vs-input
              v-if="!isEditing"
              v-model="row.unit"
              :disabled="true"
              class="w-24"
            />
            <vs-input
              v-else
              v-model="row.unit"
              :disabled="isViewMode"
              class="w-24"
              placeholder="Unit"
            />
          </template>
        </template>
        <template #plan="{ row }">
          <template v-if="row.level === 3">
            <div style="min-width: 10rem; width: 10rem;">
              <vs-input
                v-if="row.plan_type === 'text'"
                v-model="row.plan"
                :disabled="isViewMode"
                placeholder="Enter plan"
                @input="calculatePercentDiff(row)"
                style="width: 100%;"
              />
              <select-with-search
                v-else-if="row.plan_type === 'select'"
                v-model="row.plan"
                :options="row.plan_options || []"
                :disabled="isViewMode"
                placeholder="Select plan"
                hide-search
                is-object-model
                @input="calculatePercentDiff(row)"
                style="width: 100%;"
              />
              <autonumeric
                v-else-if="row.plan_type === 'number'"
                v-model="row.plan"
                :disabled="isViewMode"
                placeholder="Enter plan"
                :options="globalNumericOptions"
                @input="calculatePercentDiff(row)"
                style="width: 100%;"
              />
            </div>
          </template>
        </template>
        <template #actual="{ row }">
          <template v-if="row.level === 3">
            <div style="min-width: 10rem; width: 10rem;">
              <vs-input
                v-if="row.actual_type === 'text'"
                v-model="row.actual"
                :disabled="isViewMode"
                placeholder="Enter actual"
                @input="calculatePercentDiff(row)"
                style="width: 100%;"
              />
              <select-with-search
                v-else-if="row.actual_type === 'select'"
                v-model="row.actual"
                :options="row.actual_options || []"
                :disabled="isViewMode"
                placeholder="Select actual"
                hide-search
                is-object-model
                @input="calculatePercentDiff(row)"
                style="width: 100%;"
              />
              <autonumeric
                v-else-if="row.actual_type === 'number'"
                v-model="row.actual"
                :disabled="isViewMode"
                placeholder="Enter actual"
                :options="globalNumericOptions"
                @input="calculatePercentDiff(row)"
                style="width: 100%;"
              />
            </div>
          </template>
        </template>
        <template #percent_diff="{ row }">
          <span v-if="row.level === 3">{{ row.percent_diff }}%</span>
        </template>
        <template #remark="{ row }">
          <vs-input
            v-if="row.level === 3"
            v-model="row.remark"
            :disabled="isViewMode"
            class="w-full"
            placeholder="Enter remarks"
          />
        </template>
        <template #justification="{ row }">
          <span
            v-if="row.level === 3"
            :class="getJustificationClass(row.justificationComputed)"
            class="px-2 py-1 rounded inline-block"
          >
            {{ row.justificationComputed != null && row.justificationComputed.text != null ? row.justificationComputed.text : '' }}
          </span>
        </template>
        <template #score="{ row }">
          <span
            v-if="row.level === 3"
            :class="getJustificationClass(row.justification)"
          >
            {{
              row.score !== null && row.score !== undefined
                ? row.score >= 0
                  ? row.score
                  : `(${Math.abs(row.score)})`
                : ''
            }}
          </span>
          <span
            v-else-if="row.level === 2 || row.level === 1"
            class="font-semibold"
          >
            {{
              row.total_score !== null && row.total_score !== undefined
                ? row.total_score >= 0
                  ? Number(row.total_score).toFixed(2)
                  : `(${Math.abs(row.total_score).toFixed(2)})`
                : ''
            }}
          </span>
        </template>
        <template #weight="{ row }">
          <span v-if="row.level === 3">{{ row.weight }}%</span>
          <span
            v-else-if="row.level === 2 || row.level === 1"
            class="font-semibold"
          >
            {{ row.weight }}%
          </span>
        </template>
        <template #total_score="{ row }">
          <span
            v-if="row.level === 3"
            :class="getJustificationClass(row.justification)"
          >
            {{
              row.total_score !== null && row.total_score !== undefined
                ? row.total_score >= 0
                  ? row.total_score.toFixed(2)
                  : `(${Math.abs(row.total_score).toFixed(2)})`
                : ''
            }}
          </span>
          <span
            v-else-if="row.level === 2 || row.level === 1"
            class="font-semibold"
          >
            {{
              row.total_score !== null && row.total_score !== undefined
                ? row.total_score >= 0
                  ? row.total_score.toFixed(2)
                  : `(${Math.abs(row.total_score).toFixed(2)})`
                : ''
            }}
          </span>
        </template>
      </data-table>
    </div>

    <!-- Summary Section -->
    <div class="bg-white rounded-md p-5 mb-5">
      <p class="font-semibold text-xl mb-4">
        Summary Tug & Barge Performance
      </p>
      <data-table
        :table-header="summaryTableHeaders"
        :table-data="summaryData"
        body-height="auto"
        :loading="isBusy"
        :row-class="getSummaryRowClass"
      >
        <template #category="{ row }">
          <div class="p-3 font-semibold">
            {{ row.category }}
          </div>
        </template>
        <template #total_score="{ row }">
          <div class="p-3 font-semibold text-center">
            <span v-if="row.category === 'Total'">&nbsp;</span>
            <span v-else>
              {{
                row.total_score !== null && row.total_score !== undefined
                  ? row.total_score >= 0
                    ? row.total_score.toFixed(2)
                    : `(${Math.abs(row.total_score).toFixed(2)})`
                  : ''
              }}
            </span>
          </div>
        </template>
        <template #weight="{ row }">
          <div class="p-3 font-semibold text-center">
            {{ row.weight }}%
          </div>
        </template>
        <template #weighted_score="{ row }">
          <div class="p-3 font-semibold text-center">
            {{
              row.weighted_score !== null && row.weighted_score !== undefined
                ? row.weighted_score >= 0
                  ? row.weighted_score.toFixed(2)
                  : `(${Math.abs(row.weighted_score).toFixed(2)})`
                : ''
            }}
          </div>
        </template>
      </data-table>
      
      <div class="mt-4 flex items-center justify-between summary-status-row">
        <span class="font-semibold">
          Performance Status
        </span>
        <span
          :class="getPerformanceStatusClass()"
          class="px-4 py-2 rounded font-semibold text-white"
        >
          {{ getPerformanceStatus() }}
        </span>
      </div>
      
      <div class="mt-4 summary-legend-row">
        <p class="font-semibold mb-2">
          Legend:
        </p>
        <ul class="list-disc list-inside text-sm">
          <li>&gt;= 0 Usable</li>
          <li>&gt;= -0.2 Use with Care</li>
          <li>&lt; -0.2 Not Recommended to Use</li>
        </ul>
      </div>

      <div class="mt-4">
        <p class="font-semibold mb-2">
          Remark
        </p>
        <text-area
          v-model="summaryRemark"
          :disabled="isViewMode"
          placeholder="Enter remarks"
          class="w-full"
        />
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-end gap-4">
      <vs-button
        type="border"
        color="secondary"
        class="font-semibold"
        @click="$router.back()"
      >
        Cancel
      </vs-button>
      <vs-button
        v-if="!isViewMode"
        color="secondary"
        class="font-semibold"
        @click="submitEvaluation"
      >
        Submit Evaluation
      </vs-button>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'

import Autonumeric from '@/components/autonumeric'
import DataTable from '@/components/dataTable'
import SelectWithSearch from '@/components/selectWithSearch'
import TextArea from '@/components/textArea'
import { PAGE_TITLE } from '@/fixtures/Constants'

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title || 'Tug & Barge Performance'}`
    }
  },
  components: {
    DataTable,
    Autonumeric,
    SelectWithSearch,
    TextArea
  },
  data() {
    return {
      isBusy: false,
      isEditing: this.$route.query.isEditing === 'true' || this.$route.query.isEditing === true,
      headerData: {
        contractor_name: '',
        contract_number: '',
        contract_term: '',
        evaluation_period: ''
      },
      tableHeaders: [
        { key: 'performance_items', label: 'Performance Items', width: '250px' },
        { key: 'unit', label: 'Unit', width: '100px' },
        { key: 'plan', label: 'Plan', width: '150px' },
        { key: 'actual', label: 'Actual', width: '150px' },
        { key: 'percent_diff', label: '%/Diff', width: '75px' },
        { key: 'remark', label: 'Remark', width: '300px' },
        { key: 'justification', label: 'Justification', width: '150px' },
        { key: 'score', label: 'Score', width: '100px' },
        { key: 'weight', label: 'Weight', width: '100px' },
        { key: 'total_score', label: 'Total Score', width: '120px' }
      ],
      groupedHeaders: [
        {
          title: 'Performance Result',
          startColumn: 'plan',
          endColumn: 'remark',
          backgroundColor: '#f3f4f6'
        },
        {
          title: 'Performance Justification',
          startColumn: 'justification',
          endColumn: 'total_score',
          backgroundColor: '#f3f4f6'
        }
      ],
      performanceData: [],
      summaryData: [],
      summaryRemark: '',
      summaryTableHeaders: [
        { key: 'category', label: 'Category', width: '1175px' },
        { key: 'total_score', label: 'Score', width: '100px' },
        { key: 'weight', label: 'Weight', width: '100px' },
        { key: 'weighted_score', label: 'Total Score', width: '120px' }
      ]
    }
  },
  computed: {
    ...mapGetters({
      globalNumericOptions: 'globalNumericOptions',
      tugBargesPerformanceDetails: 'TugBargesCombination/tugBargesPerformanceDetails'
    }),
    isViewMode() {
      return !this.isEditing
    }
  },
  mounted() {
    this.fetchTugBargesPerformanceDetails()
  },
  methods: {
    async fetchTugBargesPerformanceDetails() {
      try {
        this.isBusy = true
        const id = this.$route.params.id
        const monthYear = moment(this.$route.query.month_year).format('MM-YYYY') || moment().format('MM-YYYY')
        
        const { data } = await this.$store.dispatch('TugBargesCombination/fetchTugBargesPerformanceDetails', { 
          id: id, 
          month_year: monthYear 
        })
        
        if (data) {
          if (data.owner_name) {
            this.headerData.contractor_name = data.owner_name
          }
          if (data.contract_number) {
            this.headerData.contract_number = data.contract_number
          }
          if (data.contract_term) {
            this.headerData.contract_term = data.contract_term
          }
          if (data.evaluation_period) {
            this.headerData.evaluation_period = data.evaluation_period
          }

          const sections = (data.form_data && data.form_data.sections) || data.sections || []
          this.performanceData = this.buildPerformanceData(sections)
          this.summaryData = this.buildSummaryData(sections)
          
          if (data.general_remarks) {
            this.summaryRemark = data.general_remarks
          }
        }
        this.isBusy = false
      } catch (error) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch tug barges performance details, please try again')
      }
    },
    buildPerformanceData(sections) {
      const rows = []

      sections.forEach((section, sIdx) => {
        const sectionNumber = `${sIdx + 1}`
        rows.push({
          id: `sec-${sectionNumber}`,
          level: 1,
          performance_items: `${sectionNumber}. ${section.name}`,
          unit: '',
          plan: '',
          actual: '',
          percent_diff: '',
          remark: '',
          justification: null,
          score: null,
          weight: section.weight != null ? section.weight : '',
          total_score: null,
          plan_type: null,
          actual_type: null,
          plan_options: [],
          actual_options: []
        })

        ;(section.sub_sections || []).forEach((sub, subIdx) => {
          const subNumber = `${sectionNumber}.${subIdx + 1}`
          rows.push({
            id: `sub-${subNumber}`,
            level: 2,
            performance_items: `${subNumber} ${sub.name}`,
            unit: '',
            plan: '',
            actual: '',
            percent_diff: '',
            remark: '',
            justification: null,
            score: null,
            weight: sub.weight != null ? sub.weight : '',
            total_score: null,
            plan_type: null,
            actual_type: null,
            plan_options: [],
            actual_options: []
          })

          ;(sub.items || []).forEach((item, itemIdx) => {
            const itemNumber = `${subNumber}.${itemIdx + 1}`
            const isPlanSelect = item.input_type_plan === 'Option'
            const isActualSelect = item.input_type_actual === 'Option'
            const planType = isPlanSelect ? 'select' : 'number'
            const actualType = isActualSelect ? 'select' : 'number'
            const options = (item.options || []).map(opt => ({
              text: typeof opt === 'object' ? opt.label : opt,
              value: typeof opt === 'object'
                ? (opt.value || this.normalizeJustificationValue(opt.label || opt.value || opt))
                : this.normalizeJustificationValue(opt),
              raw: opt
            }))
            const justificationOptions = (item.scoring_rules || []).map(rule => ({
              text: rule.label,
              value: this.normalizeJustificationValue(rule.label),
              min: rule.min,
              max: rule.max,
              score: rule.score
            }))

            let initialPlan = ''
            let initialActual = ''
            
            if (isPlanSelect && item.plan_value != null) {
              const mapping = item.plan_value_mapping || {}
              const planOption = options.find(opt => {
                const optValue = opt.value ? opt.value.toString().toUpperCase() : ''
                return mapping[optValue] === item.plan_value || mapping[opt.text] === item.plan_value
              })
              initialPlan = planOption || item.plan_value
            } else if (!isPlanSelect && item.plan_value != null) {
              initialPlan = item.plan_value
            }
            
            if (isActualSelect && item.actual_value != null) {
              const mapping = item.plan_value_mapping || {}
              const actualOption = options.find(opt => {
                const optValue = opt.value ? opt.value.toString().toUpperCase() : ''
                return mapping[optValue] === item.actual_value || mapping[opt.text] === item.actual_value
              })
              initialActual = actualOption || item.actual_value
            } else if (!isActualSelect && item.actual_value != null) {
              initialActual = item.actual_value
            }

            rows.push({
              id: `item-${itemNumber}`,
              level: 3,
              performance_items: `${itemNumber} ${item.name}`,
              unit: item.unit || '',
              plan: initialPlan,
              actual: initialActual,
              percent_diff: '',
              remark: item.remarks || '',
              justification: null,
              score: null,
              weight: item.weight != null ? item.weight : '',
              total_score: null,
              plan_type: planType,
              actual_type: actualType,
              plan_options: isPlanSelect ? options : [],
              actual_options: isActualSelect ? options : [],
              justification_options: justificationOptions,
              scoring_rules: item.scoring_rules || [],
              plan_value_mapping: item.plan_value_mapping || null,
              justificationComputed: null,
              justificationScoreMap: null,
              item_slug: item.slug || item.item_slug || `item-${itemNumber}`
            })
            
            const row = rows[rows.length - 1]
            if (initialPlan && initialActual) {
              this.$nextTick(() => {
                this.calculatePercentDiff(row)
              })
            }
          })
        })
      })

      return rows
    },
    buildSummaryData(sections) {
      const summary = (sections || []).map((section, idx) => ({
        category: `${idx + 1}. ${section.name}`,
        total_score: 0,
        weight: section.weight != null ? section.weight : 0,
        weighted_score: 0
      }))

      summary.push({
        category: 'Total',
        total_score: null,
        weight: 100,
        weighted_score: 0
      })

      return summary
    },
    normalizeJustificationValue(label = '') {
      return label.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '_')
    },
    calculatePercentDiff(row) {
      if (row.level !== 3) return

      const planVal = this.getNumericValue(row, row.plan, true)
      const actualVal = this.getNumericValue(row, row.actual, false)

      let diff = null
      if (
        planVal !== null && planVal !== undefined &&
        actualVal !== null && actualVal !== undefined &&
        Number(planVal) !== 0
      ) {
        diff = (Number(actualVal) / Number(planVal)) * 100
      }
      if (Number(planVal) === 0) {
        if (Number(actualVal) === 0) {
          diff = 0
        } else {
          diff = Number(actualVal) * 100
        }
      }

      this.$set(row, 'percent_diff', diff !== null ? parseFloat(diff.toFixed(1)) : '')
      this.calculateJustificationAndScore(row, diff)
    },
    getNumericValue(row, selectedValue, isPlan) {
      const isSelectSide = isPlan ? row.plan_type === 'select' : row.actual_type === 'select'
      if (!isSelectSide) {
        return selectedValue !== undefined && selectedValue !== null ? selectedValue : null
      }

      if (!selectedValue) return null

      const mapping = row.plan_value_mapping || {}
      if (Object.keys(mapping).length > 0) {
        const keyRaw = typeof selectedValue === 'object'
          ? selectedValue.raw || selectedValue.text || selectedValue.value
          : selectedValue
        const keyVal = typeof selectedValue === 'object' ? selectedValue.value : selectedValue
        const candidates = [
          keyRaw,
          keyVal,
          String(keyRaw).toUpperCase(),
          String(keyVal).toUpperCase(),
          this.normalizeJustificationValue(keyRaw || ''),
          this.normalizeJustificationValue(keyVal || '')
        ]
        const found = candidates.find(k => mapping[k] !== undefined && mapping[k] !== null)
        if (found !== undefined) {
          return mapping[found]
        }
      }

      const options = isPlan ? row.plan_options : row.actual_options
      const valueKey = typeof selectedValue === 'object' ? selectedValue.value : selectedValue
      const idx = (options || []).findIndex(opt => opt.value === valueKey)
      if (idx === -1) return null

      const normalized = valueKey.toString().toLowerCase()
      if (normalized.includes('good')) return 5
      if (normalized.includes('fair')) return 3.5
      if (normalized.includes('poor')) return 2.4

      return idx + 1
    },
    calculateJustificationAndScore(row, diff) {
      if (diff === null || diff === undefined) {
        this.$set(row, 'justificationComputed', null)
        this.$set(row, 'score', null)
        this.$set(row, 'total_score', null)
        this.recalculateParentTotals()
        return
      }

      const rules = row.scoring_rules || []
      if (!rules.length) {
        this.$set(row, 'justificationComputed', null)
        this.$set(row, 'score', null)
        this.$set(row, 'total_score', null)
        this.recalculateParentTotals()
        return
      }

      const effectiveDiff = Math.floor(Number(diff))

      const match = rules.find(rule => {
        const minOk = rule.min === null || rule.min === undefined || effectiveDiff >= rule.min
        const maxOk = rule.max === null || rule.max === undefined || effectiveDiff <= rule.max
        return minOk && maxOk
      })

      if (!match) {
        this.$set(row, 'justificationComputed', null)
        this.$set(row, 'score', null)
        this.$set(row, 'total_score', null)
        this.recalculateParentTotals()
        return
      }

      const weightNumber = Number(row.weight != null ? row.weight : 0)
      const weight = weightNumber / 100
      const totalScore = (match.score || 0) * weight

      this.$set(row, 'justificationComputed', {
        text: match.label || match.text || '',
        value: this.normalizeJustificationValue(match.label || match.text || '')
      })
      this.$set(row, 'score', match.score)
      this.$set(row, 'total_score', totalScore)

      this.recalculateParentTotals()
    },
    recalculateParentTotals() {
      const level2Totals = {}
      const level1Totals = {}

      this.performanceData.forEach(row => {
        if (row.level === 3) {
          const parts = row.performance_items.split(' ')[0].split('.')
          const level2Key = `${parts[0]}.${parts[1]}`
          if (!level2Totals[level2Key]) level2Totals[level2Key] = 0
          level2Totals[level2Key] += row.total_score || 0
        }
      })

      this.performanceData.forEach(row => {
        if (row.level === 2) {
          const key = row.performance_items.split(' ')[0]
          const sum = level2Totals[key] || 0
          this.$set(row, 'total_score', sum)

          const level1Key = key.split('.')[0]
          if (!level1Totals[level1Key]) level1Totals[level1Key] = 0
          level1Totals[level1Key] += sum
        }
      })

      this.performanceData.forEach(row => {
        if (row.level === 1) {
          const key = row.performance_items.split('.')[0]
          const sum = level1Totals[key] || 0
          this.$set(row, 'total_score', sum)
        }
      })

      const updated = this.summaryData.map(item => {
        if (item.category === 'Total') return item

        const sectionNumber = item.category.split('.')[0]

        const level1 = this.performanceData.find(
          row => row.level === 1 &&
            row.performance_items.startsWith(`${sectionNumber}.`)
        )

        const totalScore = level1?.total_score || 0
        const weighted = (Number(item.weight) / 100) * totalScore

        return {
          ...item,
          total_score: totalScore,
          weighted_score: weighted
        }
      })

      const totalWeighted = updated
        .filter(i => i.category !== 'Total')
        .reduce((acc, cur) => acc + (cur.weighted_score || 0), 0)

      this.summaryData = updated.map(item =>
        item.category === 'Total'
          ? { ...item, weighted_score: totalWeighted, total_score: totalWeighted }
          : item
      )
    },
    getJustificationClass(justification) {
      if (!justification || !justification.value) return ''
      
      const classes = {
        excellent: 'text-green-600 bg-green-100 px-2 py-1 rounded',
        good: 'text-green-500 bg-green-50 px-2 py-1 rounded',
        acceptable: 'text-blue-600 bg-blue-100 px-2 py-1 rounded',
        cautionary: 'text-orange-600 bg-orange-100 px-2 py-1 rounded',
        unacceptable: 'text-red-600 bg-red-100 px-2 py-1 rounded'
      }
      
      return classes[justification.value] || ''
    },
    getPerformanceStatus() {
      const totalScore = this.summaryData.find(s => s.category === 'Total')?.weighted_score || 0
      if (totalScore >= 0) return 'USABLE'
      if (totalScore >= -0.2) return 'USE WITH CARE'
      return 'NOT RECOMMENDED TO USE'
    },
    getPerformanceStatusClass() {
      const totalScore = this.summaryData.find(s => s.category === 'Total')?.weighted_score || 0
      if (totalScore >= 0) return 'bg-green-100 text-green-600'
      if (totalScore >= -0.2) return 'bg-orange-100 text-orange-600'
      return 'bg-red-100 text-red-600'
    },
    getRowClass(row) {
      if (row.level === 1) {
        return 'level-1'
      }
      if (row.level === 2) {
        return 'level-2'
      }
      return ''
    },
    getSummaryRowClass(row) {
      if (row.category === 'Total') {
        return 'summary-total'
      }
      return 'summary-data'
    },
    async submitEvaluation() {
      try {
        this.isBusy = true
        const totalRow = this.summaryData.find(s => s.category === 'Total')
        const overallScore = totalRow ? (totalRow.weighted_score || 0) : 0
        const operationScore = this.summaryData.find(s => s.category.includes('Operation')).weighted_score.toFixed(2) || 0
        const availabilityScore = this.summaryData.find(s => s.category.includes('Availability')).weighted_score.toFixed(2) || 0
        const performanceStatus = this.getPerformanceStatus()
        
        const formData = this.performanceData
          .filter(row => row.level === 3)
          .map(row => {
            const planValue = this.getNumericValue(row, row.plan, true)
            const actualValue = this.getNumericValue(row, row.actual, false)
            const justificationText = row.justificationComputed?.text || ''
            const justificationScore = row.score !== null && row.score !== undefined ? row.score : 0
            const weightSnapshot = row.weight !== null && row.weight !== undefined ? parseFloat(row.weight) : 0
            
            return {
              item_slug: row.item_slug || row.id,
              plan_value: planValue !== null && planValue !== undefined ? parseFloat(planValue) : 0,
              actual_value: actualValue !== null && actualValue !== undefined ? parseFloat(actualValue) : 0,
              justification: justificationText,
              justification_score: justificationScore,
              weight_snapshot: weightSnapshot,
              remarks: row.remark || null
            }
          })
        
        const payload = {
          owner_id: this.$route.params.id,
          period_date: moment(this.$route.query.month_year).format('MM-YYYY') || moment().format('MM-YYYY'),
          overall_score: parseFloat(overallScore.toFixed(2)),
          performance_status: performanceStatus,
          general_remarks: this.summaryRemark || null,
          form_data: formData,
          operation_score: operationScore,
          availability_score: availabilityScore
        }

        await this.$store.dispatch('TugBargesCombination/postTugBargesPerformance', payload)
        
        this.showSuccessNotification(this, 'Evaluation submitted successfully')
        this.$router.back()
        this.isBusy = false
      } catch (e) {

        this.showFailedNotification(this, 'Failed to submit evaluation, please try again')
        this.isBusy = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/deep/ .vs-table--tbody-table {
  .vs-table--tr {
    &.level-1 {
      background-color: #493493 !important;
      color: #ffffff !important;
      font-weight: bold !important;
    }
    &.level-2 {
      background-color: #555094 !important;
      color: #ffffff !important;
      font-weight: 600 !important;
    }
    &.summary-data {
      background-color: #493493 !important;
      color: #ffffff !important;
    }
    &.summary-total {
      background-color: #493493 !important;
      color: #ffffff !important;
    }
  }
}

.summary-status-row {
  background-color: #4CAF50;
  color: #ffffff;
  padding: 12px 16px;
  border-radius: 4px;
}

.summary-legend-row {
  background-color: #f3f4f6;
  padding: 12px 16px;
  border-radius: 4px;
}
</style>
